import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { resetRecoil, setRecoil } from "recoil-nexus";
import useJanusAudioRoom from "../../components/janus/useJanusAudioRoom";
import ApiController from "../../controller/ApiController";
import {
  interphoneAreaState,
  interphoneState,
  InterphoneType,
} from "../../recoil/interphone";
import { langState, langText } from "../../recoil/lang";
import {
  CallEndAudio,
  CallRingtoneAudio,
  CallWaitAudio,
} from "../../utils/Audio";
import {
  INTERPHONE_STATUS_CALLING,
  INTERPHONE_STATUS_CANCEL,
  INTERPHONE_STATUS_CONNECTING_WAIT,
  INTERPHONE_STATUS_NONE,
  INTERPHONE_STATUS_RECEIVER_WAIT,
  INTERPHONE_STATUS_SENDER_WAIT,
} from "../../controller/InterphoneController";
import { useQueryClient } from "@tanstack/react-query";
import { areaType } from "../../recoil/area";
import LogController from "../../controller/LogController";

let timer: any;
let waitAudioTimer: any;
let ringtoneAudioTimer: any;

export const useInterphone = () => {
  const lang = useRecoilValue(langState);
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const deviceArea: areaType | undefined = queryClient.getQueryData([
    "device-area",
  ]);

  const interphoneFlag = useRecoilValue<InterphoneType>(interphoneState);
  const interphoneArea = useRecoilValue<string | null>(interphoneAreaState);
  const [sendType, setSendType] = useState<string>("");
  const [seconds, setSeconds] = useState<number>(0);

  const { joined, error, leaveRoom } = useJanusAudioRoom({
    roomId: interphoneFlag.room_name,
    canJoin: interphoneFlag.room_name ? true : false,
    sendType: sendType,
  });

  // API
  const acceptVoiceChat = async () => {
    try {
      LogController.click({
        path: "Interphone",
        params: "interphone",
        params2: `통화 수락 버튼 클릭`,
        index: interphoneFlag.interphone_call_manage_id || -1,
      });

      const oParams = {
        manage_id: interphoneFlag.interphone_call_manage_id,
      };

      const response = await ApiController.interphone.acceptVoiceChat(oParams);

      if (response && response.data) {
        const oRes = response.data;
        console.log("acceptVoiceChat", oRes);

        if (oRes.ok && oRes.data && oRes.data.status) {
          setRecoil(interphoneState, (prev) => ({
            ...prev,
            room_name: oRes.data.room_name,
          }));
        }
      }
    } catch (err) {
      console.log("requestVoiceChat err-->", err);
    }
  };

  // 0: Timeout, 1: SenderCancel, 2: ReceiverCancel
  const cancelVoiceChat = async (type: number) => {
    try {
      LogController.click({
        path: "Interphone",
        params: "interphone",
        params2: `통화 취소 및 거절 버튼 클릭 ${
          type === INTERPHONE_STATUS_SENDER_WAIT
            ? "SENDER_CANCEL"
            : type === INTERPHONE_STATUS_RECEIVER_WAIT
            ? "RECEIVER_CANCEL"
            : type === 0 && "SENDER_TIMEOUT"
        }`,
        index: interphoneFlag.interphone_call_manage_id || -1,
      });

      const oParams = {
        action:
          type === INTERPHONE_STATUS_SENDER_WAIT
            ? "SENDER_CANCEL"
            : type === INTERPHONE_STATUS_RECEIVER_WAIT
            ? "RECEIVER_CANCEL"
            : type === 0 && "SENDER_TIMEOUT",
        manage_id: interphoneFlag.interphone_call_manage_id,
      };

      const response = await ApiController.interphone.cancelVoiceChat(oParams);

      if (response && response.data) {
        const oRes = response.data;
        console.log("cancelVoiceChat", oRes);

        if (oRes.ok && oRes.data && oRes.data.status) {
          if (type === 0) {
            resetRecoil(interphoneState);
            resetRecoil(interphoneAreaState);
          }
          return true;
        }
      }
    } catch (err) {
      console.log("requestVoiceChat err-->", err);
    }
    return false;
  };

  // 통화 나가기
  const handleLeave = () => {
    if (interphoneFlag.status === INTERPHONE_STATUS_CALLING) {
      leaveRoom();
    } else if (interphoneFlag.status === INTERPHONE_STATUS_SENDER_WAIT) {
      const isCancel = cancelVoiceChat(INTERPHONE_STATUS_SENDER_WAIT).then(
        () => {
          leaveRoom();
        }
      );
      if (!isCancel) return;
    } else if (interphoneFlag.status === INTERPHONE_STATUS_RECEIVER_WAIT) {
      const isCancel = cancelVoiceChat(INTERPHONE_STATUS_RECEIVER_WAIT).then(
        () => {
          leaveRoom();
        }
      );
      if (!isCancel) return;
    }
    resetRecoil(interphoneState);
    resetRecoil(interphoneAreaState);
  };

  // 통화 상태 title
  const getInterphoneTitle = () => {
    switch (interphoneFlag.status) {
      case INTERPHONE_STATUS_NONE:
        return langText.interphone_status_none[lang];
      case INTERPHONE_STATUS_SENDER_WAIT:
        return langText.interphone_status_sender_wait[lang];
      case INTERPHONE_STATUS_RECEIVER_WAIT:
        return langText.interphone_status_receiver_wait[lang];
      case INTERPHONE_STATUS_CALLING:
        return langText.interphone_status_calling[lang];
      case INTERPHONE_STATUS_CONNECTING_WAIT:
        return langText.interphone_status_connecting_wait[lang];
      default:
        return (
          interphoneFlag.reject_reason ||
          langText.interphone_status_cancel[lang]
        );
    }
  };

  // 통화 시간 UI
  const getCallingTime = () => {
    const hour = Math.floor(seconds / 3600);
    const minute = Math.floor((seconds % 3600) / 60);
    const second = seconds % 60;

    return [hour, minute, second]
      .map((item) => String(item).padStart(2, "0"))
      .join(":");
  };

  // Audio 재생
  const handleAudioWaitPlay = () => {
    waitAudioTimer = setTimeout(() => {
      if (interphoneFlag.status === INTERPHONE_STATUS_SENDER_WAIT) {
        CallWaitAudio.currentTime = 0;
        CallWaitAudio.play().catch((error) => {
          console.error("Error playing audio", error);
        });
      } else {
        CallWaitAudio.pause();
        CallWaitAudio.currentTime = 0;
        clearTimeout(waitAudioTimer);
        waitAudioTimer = null;
      }
    }, 1200);
  };

  const handleAudioRingtonePlay = () => {
    ringtoneAudioTimer = setTimeout(() => {
      if (interphoneFlag.status === INTERPHONE_STATUS_RECEIVER_WAIT) {
        CallRingtoneAudio.currentTime = 0;
        CallRingtoneAudio.play().catch((error) => {
          console.error("Error playing audio", error);
        });
      } else {
        CallRingtoneAudio.pause();
        CallRingtoneAudio.currentTime = 0;
        clearTimeout(ringtoneAudioTimer);
        ringtoneAudioTimer = null;
      }
    }, 3000);
  };

  // cleanup
  useEffect(() => {
    return () => {
      clearTimeout(timer);
      timer = null;
    };
  }, []);

  useEffect(() => {
    if (interphoneFlag.status === INTERPHONE_STATUS_SENDER_WAIT) {
      setSendType("SENDER");
    } else if (interphoneFlag.status === INTERPHONE_STATUS_RECEIVER_WAIT) {
      setSendType("RECEIVER");
    }

    // 60초 후 타임아웃
    if (interphoneFlag.status === INTERPHONE_STATUS_SENDER_WAIT) {
      timer = setTimeout(() => {
        if (interphoneFlag.status === INTERPHONE_STATUS_SENDER_WAIT) {
          cancelVoiceChat(0);
          timer = null;
        }
      }, 60 * 1000);
    } else {
      clearTimeout(timer);
      timer = null;
    }

    // 통화 시간 변경
    let interval: any = null;

    if (interphoneFlag.status === INTERPHONE_STATUS_CALLING) {
      interval = setInterval(() => {
        setSeconds((prev) => prev + 1);
      }, 1000);
    } else {
      clearInterval(interval);
      interval = null;
    }

    // 전화 대기 효과음
    if (interphoneFlag.status === INTERPHONE_STATUS_SENDER_WAIT) {
      handleAudioWaitPlay();
      CallWaitAudio.addEventListener("ended", handleAudioWaitPlay);
    } else {
      clearTimeout(waitAudioTimer);
      waitAudioTimer = null;
      CallWaitAudio.pause();
      CallWaitAudio.currentTime = 0;
    }

    // 컬러링
    if (interphoneFlag.status === INTERPHONE_STATUS_RECEIVER_WAIT) {
      handleAudioRingtonePlay();
      CallRingtoneAudio.addEventListener("ended", handleAudioRingtonePlay);
    } else {
      clearTimeout(ringtoneAudioTimer);
      ringtoneAudioTimer = null;
      CallRingtoneAudio.pause();
      CallRingtoneAudio.currentTime = 0;
    }

    // 통화 종료 효과음
    if (
      interphoneFlag.status === INTERPHONE_STATUS_NONE ||
      interphoneFlag.status === INTERPHONE_STATUS_CANCEL
    ) {
      CallEndAudio.currentTime = 0;
      CallEndAudio.play();

      setTimeout(() => {
        resetRecoil(interphoneState);
        resetRecoil(interphoneAreaState);
      }, 2000);
    }

    return () => {
      CallWaitAudio.removeEventListener("ended", handleAudioWaitPlay);
      CallRingtoneAudio.removeEventListener("ended", handleAudioRingtonePlay);

      clearInterval(interval);
      interval = null;

      CallWaitAudio.pause();
      CallWaitAudio.currentTime = 0;

      CallEndAudio.pause();
      CallEndAudio.currentTime = 0;

      CallRingtoneAudio.pause();
      CallRingtoneAudio.currentTime = 0;

      clearTimeout(waitAudioTimer);
      waitAudioTimer = null;

      clearTimeout(ringtoneAudioTimer);
      ringtoneAudioTimer = null;
    };
  }, [interphoneFlag.status]);

  useEffect(() => {
    if (!interphoneFlag.interphone_call_manage_id) {
      navigate(-1);
    }
  }, [interphoneFlag.interphone_call_manage_id]);

  useEffect(() => {
    console.log("========== janus ", joined, " ", error, "============");
    console.log("========== props ", interphoneFlag.room_name, "============");
  }, [joined, error, sendType, interphoneFlag]);

  return {
    interphoneArea,
    interphoneFlag,
    deviceArea,
    lang,
    joined,
    error,
    getCallingTime,
    acceptVoiceChat,
    handleLeave,
    getInterphoneTitle,
  };
};
